import { useState, useMemo } from 'react';
import { Search, Database, AlertCircle, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { useDataSources } from '@/hooks/use-data-sources';
import type { DataSource } from '@/types/data-source';
import { SchemaPreview } from './SchemaPreview';
import { cn } from '@/lib/utils';

interface DataSourceSelectorProps {
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  maxSelection?: number;
}

export function DataSourceSelector({
  selectedIds,
  onSelectionChange,
  maxSelection = 5,
}: DataSourceSelectorProps) {
  const [search, setSearch] = useState('');
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [showOnlyReady, setShowOnlyReady] = useState(false);

  const { data: dsData, isLoading, isError } = useDataSources({
    page: 1,
    limit: 100,
  });

  const dataSources = dsData?.dataSources ?? [];

  const getSuccessfulExtractions = (ds: DataSource): number =>
    ds.successfulExtractions ?? ds.stats?.successfulExtractions ?? 0;
  const getFailedExtractions = (ds: DataSource): number =>
    ds.failedExtractions ?? ds.stats?.failedExtractions ?? 0;
  const getTotalExtractions = (ds: DataSource): number =>
    ds.totalExtractions ?? ds.stats?.totalExtractions ?? 0;

  const stats = useMemo(() => {
    const ready = dataSources.filter((ds: DataSource) => getSuccessfulExtractions(ds) > 0).length;
    const total = dataSources.length;
    const noData = dataSources.filter((ds: DataSource) => getTotalExtractions(ds) === 0).length;
    const failed = dataSources.filter((ds: DataSource) =>
      getFailedExtractions(ds) > 0 && getSuccessfulExtractions(ds) === 0
    ).length;
    return { ready, total, noData, failed };
  }, [dataSources]);

  const filtered = useMemo(() => {
    let result = dataSources;

    // Filter by search query
    const query = search.trim().toLowerCase();
    if (query) {
      result = result.filter(
        (ds: DataSource) =>
          ds.name.toLowerCase().includes(query) ||
          ds.type.toLowerCase().includes(query),
      );
    }

    // Filter by ready status
    if (showOnlyReady) {
      result = result.filter((ds: DataSource) => getSuccessfulExtractions(ds) > 0);
    }

    return result;
  }, [dataSources, search, showOnlyReady]);

  // Helper to get extraction status
  function getExtractionStatus(ds: DataSource): {
    status: 'ready' | 'no-data' | 'failed' | 'pending';
    icon: typeof CheckCircle2;
    label: string;
    variant: 'default' | 'secondary' | 'destructive' | 'outline';
  } {
    const successfulExtractions = getSuccessfulExtractions(ds);
    const failedExtractions = getFailedExtractions(ds);
    const totalExtractions = getTotalExtractions(ds);

    if (successfulExtractions > 0) {
      return {
        status: 'ready',
        icon: CheckCircle2,
        label: `${successfulExtractions} extracted`,
        variant: 'default',
      };
    }
    if (failedExtractions > 0 && successfulExtractions === 0) {
      return {
        status: 'failed',
        icon: XCircle,
        label: 'Extraction failed',
        variant: 'destructive',
      };
    }
    if (totalExtractions === 0) {
      return {
        status: 'no-data',
        icon: AlertTriangle,
        label: 'No data yet',
        variant: 'secondary',
      };
    }
    return {
      status: 'pending',
      icon: AlertCircle,
      label: 'Processing',
      variant: 'outline',
    };
  }

  function toggleSelection(id: string): void {
    const ds = dataSources.find((d: DataSource) => d.id === id);
    // Only allow selection of datasources with successful extractions
    if (!ds || getSuccessfulExtractions(ds) === 0) {
      return;
    }

    if (selectedIds.includes(id)) {
      onSelectionChange(selectedIds.filter((s) => s !== id));
      if (previewId === id) setPreviewId(null);
    } else if (selectedIds.length < maxSelection) {
      onSelectionChange([...selectedIds, id]);
    }
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-4 text-sm text-muted-foreground">
        <AlertCircle className="h-8 w-8 mb-2 text-destructive" />
        <p>Failed to load datasources</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="p-3 border-b space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Data Sources</h3>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              <CheckCircle2 className="h-3 w-3 mr-1 text-green-600" />
              {stats.ready}/{stats.total} ready
            </Badge>
          </div>
        </div>

        <div className="relative">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search datasources..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-9 text-sm"
          />
        </div>

        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1">
            <Badge
              variant={selectedIds.length > 0 ? 'default' : 'secondary'}
              className="text-xs"
            >
              {selectedIds.length}/{maxSelection} selected
            </Badge>
          </div>
          <label className="flex items-center gap-1.5 cursor-pointer">
            <Checkbox
              checked={showOnlyReady}
              onCheckedChange={(checked) => setShowOnlyReady(checked as boolean)}
              className="h-3.5 w-3.5"
            />
            <span className="text-xs text-muted-foreground">Ready only</span>
          </label>
        </div>

        {stats.noData > 0 && (
          <p className="text-xs text-amber-600 bg-amber-50 dark:bg-amber-950/20 rounded px-2 py-1">
            <AlertTriangle className="h-3 w-3 inline mr-1" />
            {stats.noData} datasource{stats.noData > 1 ? 's' : ''} need extraction
          </p>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="p-2 space-y-1">
          {isLoading ? (
            <div className="p-4 text-center text-sm text-muted-foreground">
              Loading datasources...
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-4 text-center text-sm text-muted-foreground space-y-2">
              {dataSources.length === 0 ? (
                <>
                  <AlertCircle className="h-8 w-8 mx-auto text-muted-foreground/50" />
                  <p>No datasources available.</p>
                  <p className="text-xs">Create a datasource first.</p>
                </>
              ) : showOnlyReady && stats.ready === 0 ? (
                <>
                  <AlertTriangle className="h-8 w-8 mx-auto text-amber-500" />
                  <p>No datasources with extracted data.</p>
                  <p className="text-xs">Run extraction jobs first to use AI chat.</p>
                </>
              ) : (
                <>
                  <Search className="h-8 w-8 mx-auto text-muted-foreground/50" />
                  <p>No matching datasources found.</p>
                </>
              )}
            </div>
          ) : (
            filtered.map((ds: DataSource) => {
              const isSelected = selectedIds.includes(ds.id);
              const extractionInfo = getExtractionStatus(ds);
              const hasData = getSuccessfulExtractions(ds) > 0;
              const isAtLimit = !isSelected && selectedIds.length >= maxSelection;
              const isDisabled = !hasData || isAtLimit;

              return (
                <div
                  key={ds.id}
                  className={cn(
                    'flex items-start gap-2 p-2 rounded-md transition-colors',
                    hasData && 'cursor-pointer hover:bg-accent',
                    isSelected && 'bg-accent',
                    !hasData && 'opacity-50 cursor-not-allowed'
                  )}
                  onClick={() => hasData && toggleSelection(ds.id)}
                >
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => toggleSelection(ds.id)}
                    disabled={isDisabled}
                    className="mt-0.5"
                  />
                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex items-center gap-1.5">
                      <Database className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <span className="text-sm font-medium truncate">{ds.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">{ds.type}</span>
                      <Badge
                        variant={extractionInfo.variant}
                        className="text-xs h-5 px-1.5 flex items-center gap-1"
                      >
                        <extractionInfo.icon className="h-3 w-3" />
                        {extractionInfo.label}
                      </Badge>
                    </div>
                    {!hasData && (
                      <p className="text-xs text-muted-foreground italic">
                        Run extraction first to use with AI
                      </p>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {!previewId && selectedIds.length > 0 && (
        <div className="border-t">
          <SchemaPreview dataSourceId={selectedIds[0]} />
        </div>
      )}
    </div>
  );
}
